import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { GoogleMap, Marker, Circle } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";

const radius = 100;
const defaultZoom = 15;
const fallbackZoom = 4;
const defaultMarkerUrl = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png";

// Default USA center
const USA_CENTER = { lat: 37.0902, lng: -95.7129 };

const ProfileLocationMap = ({ lat, lng }) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState(t("locationMap.loadingAddress"));

  const hasValidLocation = lat && lng;
  const mapLat = hasValidLocation ? lat : USA_CENTER.lat;
  const mapLng = hasValidLocation ? lng : USA_CENTER.lng;

  const getMarkerIcon = useCallback(() => {
    if (!window.google) {
      return { url: defaultMarkerUrl };
    }

    return {
      url: defaultMarkerUrl,
      scaledSize: new window.google.maps.Size(25, 41),
      anchor: new window.google.maps.Point(12, 41),
    };
  }, []);

  // Reverse Geocode when valid location exists
  useEffect(() => {
    if (!hasValidLocation) {
      setAddress(t("locationMap.locationNotAvailable"));
      return;
    }

    const fetchAddress = async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
        );

        const result = await res.json();
        setAddress(result.display_name || t("locationMap.addressNotFound"));
      } catch {
        setAddress(t("locationMap.addressNotFound"));
      }
    };

    fetchAddress();
  }, [lat, lng, hasValidLocation, t]);

  return (
    <Box>
      {/* MAP */}
      <Box
        sx={{
          height: 300,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #ddd",
          mb: 2,
        }}
      >
        <GoogleMap
          center={{ lat: mapLat, lng: mapLng }}
          zoom={hasValidLocation ? defaultZoom : fallbackZoom}
          mapContainerStyle={{ width: "100%", height: "100%" }}
          options={{
            scrollwheel: false,
            draggable: false,
            disableDoubleClickZoom: true,
            zoomControl: false,
            disableDefaultUI: true,
            keyboardShortcuts: false,
            gestureHandling: "none",
          }}
        >
          {/* only show marker/circle IF actual worker location exists */}
          {hasValidLocation && (
            <>
              <Marker
                position={{ lat: mapLat, lng: mapLng }}
                icon={getMarkerIcon()}
              />

              <Circle
                center={{ lat: mapLat, lng: mapLng }}
                radius={radius}
                options={{
                  strokeColor: "#1E73BE",
                  fillColor: "#1E73BE",
                  strokeOpacity: 1,
                  fillOpacity: 0.2,
                }}
              />
            </>
          )}
        </GoogleMap>
      </Box>

      {/* READ-ONLY ADDRESS FIELD */}
      <textarea
        readOnly
        value={address}
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "16px",
          border: "1px solid #ccc",
          borderRadius: "8px",
          marginTop: "10px",
          backgroundColor: "#f4f4f4",
          fontFamily: "Poppins, sans-serif",
          resize: "vertical",
          overflowY: "auto",
          lineHeight: "1.4",
        }}
      />
    </Box>
  );
};

export default ProfileLocationMap;
